import { useState } from 'react';
import classnames from 'classnames/bind';
import { useTranslation } from 'react-i18next';
import { QueryState } from 'app/lib/constants';
import { useDispatch, useSelector, useToggleState } from 'app/lib/hooks';
import {
    doCancelQuery,
    selectExecutingQueryCount,
    selectQueriesWithMetadata,
    selectQueuedQueryCount
} from 'app/store/snowflakeQueries';
import { timeRenderer } from 'app/lib/utils/columnRenderer.jsx';
import { Button, ButtonEdge, ButtonSize, ButtonVariant } from 'app/components/core/Button';
import { Icon } from 'app/components/core/Icon';
import { Loading, LoadingSize } from 'app/components/core/Loading';
import { SortDirection, Table, TableVariant } from 'app/components/core/Table';
import { CancelAllQueriesConfirmationDialog } from 'app/components/features/CancelAllQueriesConfirmationDialog';
import { Context } from './Context.jsx';
import { Duration } from './Duration.jsx';
import { Header } from './Header.jsx';
import * as styles from './WarehouseLoadDialog.css';
import { SqlQueryDialog } from './SqlQueryDialog.jsx';

const cx = classnames.bind(styles);

const icon = (variant, title) => (
    <div className={styles.icon}>
        <Icon variant={variant} title={title} />
    </div>
);

export function QueryList({ log, onCancel }) {
    const { t } = useTranslation('components', { keyPrefix: 'WarehouseLoadDialog.queries' });
    const { t: tCommon } = useTranslation('common');
    const dispatch = useDispatch();
    const queries = useSelector(selectQueriesWithMetadata);
    const running = useSelector(selectExecutingQueryCount);
    const queued = useSelector(selectQueuedQueryCount);
    const [isConfirming, toggleConfirmation] = useToggleState();
    const [sqlDetails, setSqlDetails] = useState(null);

    const queryStateRenderer = (state) => {
        switch (state) {
            case QueryState.RUNNING:
                return <Loading size={LoadingSize.SMALL} />;
            case QueryState.CANCELED:
                return icon('canceled-solid', tCommon('canceled'));
            case QueryState.SUCCESS:
                return icon('check-circle-solid', tCommon('succeeded'));
            case QueryState.FAILED:
            case QueryState.INVALID:
                return icon('error-solid', tCommon('failed'));
            case QueryState.STALE:
            case QueryState.IDLE:
            default:
                return icon('minus', tCommon('unknown'));
        }
    };

    function logConfirmation() {
        log('Cancelled all of their queries.');
    }

    function handleCancelQuery(context) {
        dispatch(doCancelQuery({ id: context.queryId, queryKey: context.queryKey }));
        log(`Cancelled single query "${context.queryId}."`);
    }

    function handleShowSql({ queryId, sql }) {
        setSqlDetails({ queryId, sql });
    }

    function handleCloseSql() {
        setSqlDetails(null);
    }

    const columns = [
        {
            field: 'queryId',
            label: t('columns.id')
        },
        {
            field: 'state',
            label: t('columns.status'),
            style: { width: '1%' },
            renderer: queryStateRenderer
        },
        {
            field: 'queryId',
            key: 'context',
            label: t('columns.context'),
            renderer: (queryId) => <Context queryId={queryId} />
        },
        {
            field: 'startedAt',
            label: t('columns.started'),
            renderer: timeRenderer
        },
        {
            field: 'queryId',
            key: 'duration',
            label: t('columns.duration'),
            renderer: (queryId) => <Duration queryId={queryId} />
        },
        {
            field: 'state',
            key: 'cancel',
            label: '',
            style: { width: '1%' },
            renderer: (state, context) => (
                <div className={cx('query-actions')}>
                    <Button
                        onClick={() => handleShowSql(context)}
                        size={ButtonSize.MEDIUM}
                        label={t('sql')}
                        icon="op-sql"
                    />
                    <Button
                        onClick={() => handleCancelQuery(context)}
                        size={ButtonSize.MEDIUM}
                        label={t('cancel')}
                        icon="close"
                        disabled={state !== QueryState.RUNNING}
                    />
                </div>
            )
        }
    ];

    return (
        <section className={cx('content-pane')}>
            <Header icon="snowflake" title={t('header')}>
                <Button
                    size={ButtonSize.SMALL}
                    edge={ButtonEdge.FLAT}
                    variant={ButtonVariant.SECONDARY_BLUE}
                    disabled={running + queued === 0}
                    onClick={toggleConfirmation}>
                    {t('cancelAll')}
                </Button>
                <Button
                    variant={ButtonVariant.SECONDARY_BLUE}
                    size={ButtonSize.SMALL}
                    edge={ButtonEdge.FLAT}
                    onClick={onCancel}>
                    {t('back')}
                </Button>
            </Header>
            <Table
                className={cx('table')}
                data={queries}
                columns={columns}
                rowKey="queryId"
                variant={TableVariant.COMPACT}
                sortBy="startedAt"
                sortDir={SortDirection.DESC}
            />
            {isConfirming && (
                <CancelAllQueriesConfirmationDialog
                    onConfirm={logConfirmation}
                    onClose={toggleConfirmation}
                />
            )}
            {sqlDetails && (
                <SqlQueryDialog
                    queryId={sqlDetails.queryId}
                    sql={sqlDetails.sql}
                    onClose={handleCloseSql}
                />
            )}
        </section>
    );
}
